// Brand-specific SEO configuration for dual-brand pest control platform
import { BrandType } from "../contexts/BrandingContext";

export interface SEOConfig {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogImage?: string;
  schemaType?:
    | "LocalBusiness"
    | "WebPage"
    | "Service"
    | "Article"
    | "SoftwareApplication"
    | "Organization";
  schemaData?: any;
  robots?: string;
}

// Comprehensive SEO configuration for both brands
export const brandSeoConfig: Record<BrandType, Record<string, SEOConfig>> = {
  // Aegis360 - B2B SaaS Platform Configuration
  aegis360: {
    // Core Pages
    "/": {
      title:
        "Aegis360 - Modern Pest Control Business Software | Increase Profit & Efficiency",
      description:
        "Transform your pest control business with Aegis360's AI-powered SaaS platform. Streamline operations, automate scheduling, boost customer satisfaction, and increase profits by up to 40%.",
      keywords:
        "pest control software, pest management software, pest control business management, pest control SaaS, pest control automation, pest control CRM, technician scheduling software, pest control invoicing",
      schemaType: "SoftwareApplication",
    },
    "/pricing": {
      title: "Aegis360 Pricing Plans | Pest Control Software Subscription",
      description:
        "Flexible pricing plans for pest control businesses of all sizes. Start with our free trial and scale with Professional and Enterprise tiers. ROI-focused features included.",
      keywords:
        "pest control software pricing, pest management software cost, SaaS pest control plans, business software pricing, pest control subscription",
      schemaType: "WebPage",
    },
    "/book-demo": {
      title: "Request Demo - Aegis360 Pest Control Software | See It In Action",
      description:
        "Schedule a personalized demo of Aegis360's pest control management platform. See how our AI-powered features can transform your business operations and increase efficiency.",
      keywords:
        "pest control software demo, pest management platform demo, business software demonstration, pest control SaaS trial",
      schemaType: "Service",
    },
    "/free-trial": {
      title: "Free Trial - Aegis360 Pest Control Software | 30-Day Access",
      description:
        "Start your 30-day free trial of Aegis360 pest control software. Full access to all features, no credit card required. Experience the future of pest management.",
      keywords:
        "pest control software trial, free pest management software, pest control SaaS trial, business software trial",
      schemaType: "Service",
    },
    "/customer-management": {
      title: "Customer Management & CRM | Aegis360 Pest Control Software",
      description:
        "Comprehensive customer relationship management for pest control businesses. Track clients, manage communications, automate follow-ups, and boost retention rates.",
      keywords:
        "pest control CRM, customer management software, pest control client tracking, pest business customer database",
      schemaType: "SoftwareApplication",
    },
    "/analytics-dashboard": {
      title: "Business Analytics Dashboard | Aegis360 Performance Insights",
      description:
        "Real-time business analytics and performance metrics for pest control companies. Track KPIs, monitor technician performance, and make data-driven decisions.",
      keywords:
        "pest control analytics, business intelligence software, pest control metrics, technician performance tracking",
      schemaType: "SoftwareApplication",
    },
    "/workflow-admin": {
      title: "Workflow Automation | Aegis360 Business Process Management",
      description:
        "Automate your pest control business workflows with AI-powered job assignment, route optimization, and intelligent business process management.",
      keywords:
        "pest control automation, workflow management software, business process automation, pest control operations",
      schemaType: "SoftwareApplication",
    },
    "/about-us": {
      title: "About Aegis360 | Revolutionary Pest Control Software Company",
      description:
        "Learn about Aegis360's mission to transform the pest control industry through innovative SaaS solutions. Founded by industry experts with decades of experience.",
      keywords:
        "Aegis360 company, pest control software company, pest management technology, SaaS pest control",
      schemaType: "Organization",
    },
    "/contact": {
      title: "Contact Aegis360 | Pest Control Software Support & Sales",
      description:
        "Get in touch with Aegis360 for sales inquiries, technical support, or partnership opportunities. Our team is ready to help transform your pest control business.",
      keywords:
        "Aegis360 contact, pest control software support, pest management software sales, business software contact",
      schemaType: "WebPage",
    },
  },

  // Greenshield - B2C Local Service Configuration
  greenshield: {
    // Core Pages
    "/": {
      title:
        "Greenshield Pest Management | Fast, Effective & Eco-Friendly Pest Control UK",
      description:
        "Professional pest control services across the UK. Same-day emergency response, eco-friendly treatments, and 6-month guarantee. Call 07913024492 for immediate assistance.",
      keywords:
        "pest control UK, exterminator UK, pest removal services, eco-friendly pest control, emergency pest control, residential pest control, commercial pest control",
      schemaType: "LocalBusiness",
    },
    "/pricing": {
      title: "Pest Control Prices UK | Transparent Pricing | Greenshield",
      description:
        "Competitive pest control pricing with no hidden fees. Residential services from £89, commercial solutions available. Free quotes and same-day service across the UK.",
      keywords:
        "pest control prices UK, pest control cost, exterminator prices, pest removal cost, pest control quotes UK",
      schemaType: "Service",
    },
    "/residential-services": {
      title:
        "Residential Pest Control Services | Home Pest Removal | Greenshield",
      description:
        "Professional home pest control services. Safe for children and pets, same-day service available, 6-month guarantee. Protecting UK families since 2020.",
      keywords:
        "residential pest control, home pest removal, domestic pest control, house pest control UK, family safe pest control",
      schemaType: "Service",
    },
    "/commercial-solutions": {
      title:
        "Commercial Pest Control UK | Business Pest Management | Greenshield",
      description:
        "Comprehensive commercial pest control solutions for businesses. Food safety certified, scheduled maintenance programs, and detailed documentation for compliance.",
      keywords:
        "commercial pest control, business pest management, office pest control, restaurant pest control, warehouse pest control UK",
      schemaType: "Service",
    },
    "/emergency-pest-control": {
      title: "24/7 Emergency Pest Control UK | Same-Day Response | Greenshield",
      description:
        "Urgent pest problems? Our emergency pest control service provides rapid 24/7 response across the UK. Same-day visits available. Call 07913024492 now.",
      keywords:
        "emergency pest control, 24/7 pest control, urgent pest removal, same day pest control, emergency exterminator UK",
      schemaType: "Service",
    },
    "/specialist-services": {
      title:
        "Specialist Pest Control Services | Expert Solutions | Greenshield",
      description:
        "Expert specialist pest control including bird control, wasp removal, bed bug treatment, and wildlife management. Professional solutions for unique pest challenges.",
      keywords:
        "specialist pest control, bird control, wasp removal, bed bug treatment, wildlife control, rodent proofing UK",
      schemaType: "Service",
    },
    "/hygiene-services": {
      title:
        "Professional Hygiene Services | Sanitization & Deep Cleaning | Greenshield",
      description:
        "Comprehensive hygiene and sanitization services. Deep cleaning, air quality management, washroom solutions, and surface protection for ultimate cleanliness.",
      keywords:
        "hygiene services, sanitization services, deep cleaning services, air quality management, washroom solutions UK",
      schemaType: "Service",
    },
    "/industrial-solutions": {
      title:
        "Industrial Pest Control UK | Large-Scale Facility Management | Greenshield",
      description:
        "Advanced industrial pest control solutions with smart monitoring, AI analytics, and comprehensive facility management. Serving large-scale operations across the UK.",
      keywords:
        "industrial pest control, facility pest management, warehouse pest control, factory pest control, large scale pest control UK",
      schemaType: "Service",
    },
    "/quote": {
      title:
        "Free Pest Control Quote UK | Professional Assessment | Greenshield",
      description:
        "Get your free, no-obligation pest control quote. Professional property inspection, detailed estimate, and customized treatment plan. Same-day response available.",
      keywords:
        "free pest control quote, pest control estimate, pest inspection quote, property pest assessment UK",
      schemaType: "Service",
    },
    "/contact": {
      title: "Contact Greenshield Pest Management | 24/7 Emergency Service UK",
      description:
        "Contact our pest control experts for immediate assistance. 24/7 emergency service, free consultations, and rapid response. Call 07913024492 for instant support.",
      keywords:
        "contact pest control, pest control phone number, emergency pest service, pest control consultation UK",
      schemaType: "WebPage",
    },
    "/about-us": {
      title:
        "About Greenshield Pest Management | Eco-Friendly Pest Control Experts UK",
      description:
        "Learn about Greenshield's mission, values, and innovative approach to eco-friendly pest control. Founded by Iredia Anthony with science-driven solutions and 5-star service.",
      keywords:
        "about Greenshield, pest control company UK, eco-friendly exterminator, pest management team, sustainable pest control",
      schemaType: "Organization",
    },
    "/pest-risk-quiz": {
      title:
        "Interactive Pest Risk Assessment Quiz | Evaluate Your Property | Greenshield",
      description:
        "Take our comprehensive 17-question pest risk assessment to evaluate your property's vulnerability. Get personalized recommendations and risk analysis with AI-powered insights.",
      keywords:
        "pest risk assessment, property pest evaluation, pest vulnerability quiz, pest prevention assessment, pest risk calculator UK",
      schemaType: "WebPage",
    },
    "/seasonal-prevention-calendar": {
      title:
        "Seasonal Pest Prevention Calendar | Year-Round Protection | Greenshield",
      description:
        "Personalized monthly pest prevention schedules based on seasonal patterns. Stay ahead of pest activity with our intelligent prevention calendar.",
      keywords:
        "seasonal pest prevention, pest calendar, monthly pest control, seasonal pest protection, year-round pest management UK",
      schemaType: "Service",
    },
    "/eco-recommendation-engine": {
      title:
        "Eco-Friendly Pest Control Solutions | Sustainable Treatments | Greenshield",
      description:
        "AI-powered sustainable pest control recommendation system. Chemical-free options, organic treatments, and environmentally safe solutions for conscious consumers.",
      keywords:
        "eco-friendly pest control, sustainable pest management, organic pest control, chemical-free pest treatment, green pest control UK",
      schemaType: "Service",
    },
    "/ai-intelligence": {
      title:
        "AI-Powered Pest Control Technology | Smart Detection & Monitoring | Greenshield",
      description:
        "Advanced AI technology for pest control including smart monitoring, automated detection, predictive analytics, and intelligent treatment recommendations.",
      keywords:
        "AI pest control, smart pest monitoring, automated pest detection, pest control technology, intelligent pest management UK",
      schemaType: "Service",
    },
    "/terms-of-service": {
      title: "Terms of Service | Greenshield Pest Management",
      description:
        "Terms and conditions for Greenshield Pest Management services. Professional pest control service terms and customer agreements.",
      keywords:
        "pest control terms, service terms, pest control conditions, pest management agreement UK",
      schemaType: "WebPage",
      robots: "noindex, follow",
    },
    "/privacy-policy": {
      title: "Privacy Policy | Greenshield Pest Management",
      description:
        "Privacy policy and data protection information for Greenshield Pest Management customers and website visitors.",
      keywords:
        "privacy policy, data protection, pest control privacy, customer data UK",
      schemaType: "WebPage",
      robots: "noindex, follow",
    },
    "/service-areas": {
      title: "Pest Control Service Areas UK | Coverage Map | Greenshield",
      description:
        "Greenshield Pest Management covers all major UK towns and cities. Find expert pest control in your area — same-day service available across our full service region.",
      keywords:
        "pest control near me, local pest control UK, pest control service areas, pest control coverage UK, exterminator near me",
      schemaType: "Service",
    },
    "/blog": {
      title: "Pest Control Blog | Tips, News & Advice | Greenshield",
      description:
        "Expert pest control advice, seasonal tips, treatment guides, and industry news from the Greenshield team. Stay informed and keep pests out.",
      keywords:
        "pest control blog, pest control tips, pest prevention advice, pest control news UK, exterminator blog",
      schemaType: "WebPage",
    },
    "/news": {
      title: "Pest Control News & Updates | Greenshield Pest Management",
      description:
        "Latest news and updates from Greenshield Pest Management. Industry insights, product launches, and company announcements.",
      keywords:
        "pest control news, pest management updates, greenshield news, pest industry UK",
      schemaType: "WebPage",
    },
    "/landing": {
      title:
        "Professional Pest Control Services UK | Greenshield Pest Management",
      description:
        "Get fast, effective, and eco-friendly pest control anywhere in the UK. Free quotes, same-day service, and a 6-month guarantee. Contact Greenshield today.",
      keywords:
        "pest control UK, professional exterminator, pest removal UK, eco-friendly pest control, same day pest control",
      schemaType: "LocalBusiness",
    },
    "/seasonal-forecast": {
      title: "UK Seasonal Pest Forecast | Upcoming Pest Activity | Greenshield",
      description:
        "Stay ahead of pest season with Greenshield's UK seasonal pest forecast. Monthly pest activity predictions, prevention tips, and treatment schedules.",
      keywords:
        "seasonal pest forecast UK, pest season predictions, monthly pest activity, pest calendar UK, spring pest control",
      schemaType: "WebPage",
    },
    "/education": {
      title:
        "Pest Control Education | Learn About Pests & Prevention | Greenshield",
      description:
        "Educational resources on pest identification, biology, prevention, and treatment. Empowering homeowners and businesses with pest control knowledge.",
      keywords:
        "pest control education, pest identification, pest prevention guide, pest biology UK, learn about pests",
      schemaType: "WebPage",
    },
    "/education-hub": {
      title:
        "Pest Control Education Hub | Guides, Videos & Resources | Greenshield",
      description:
        "Your central resource for pest control knowledge. Browse guides, watch tutorials, and access expert content to protect your home or business from pests.",
      keywords:
        "pest control education hub, pest guides, pest control tutorials, pest management resources UK",
      schemaType: "WebPage",
    },
    "/testimonials": {
      title: "Customer Testimonials | Greenshield Pest Management Reviews UK",
      description:
        "Read verified testimonials from Greenshield Pest Management customers. 5-star reviews from homeowners and businesses across the UK.",
      keywords:
        "pest control testimonials, greenshield reviews, pest control customer reviews, exterminator testimonials UK",
      schemaType: "WebPage",
    },
    "/enterprise-trust": {
      title:
        "Enterprise Pest Control | Trusted by Leading UK Businesses | Greenshield",
      description:
        "Greenshield's enterprise pest control solutions. Compliance documentation, account management, and scalable programs trusted by major UK enterprises.",
      keywords:
        "enterprise pest control, corporate pest management, large business pest control, pest compliance UK",
      schemaType: "Service",
    },
    "/industrial-services": {
      title: "Industrial Pest Control Services UK | Greenshield",
      description:
        "Comprehensive industrial pest control for factories, warehouses, and large facilities. Smart monitoring, regulatory compliance, and scheduled maintenance.",
      keywords:
        "industrial pest control services, factory pest control, warehouse pest management, industrial exterminator UK",
      schemaType: "Service",
    },
    "/emergency-services": {
      title:
        "Emergency Pest Control Services | 24/7 Rapid Response | Greenshield",
      description:
        "24/7 emergency pest control services across the UK. Call now for immediate dispatch — rats, wasps, ants, and more handled fast. Same-day guaranteed.",
      keywords:
        "emergency pest services, urgent pest control UK, 24 hour exterminator, rapid pest response, call pest control now",
      schemaType: "Service",
    },
    "/ai-pest-demo": {
      title:
        "AI Pest Detection Demo | Try Our Smart Pest Identifier | Greenshield",
      description:
        "Try Greenshield's AI-powered pest identification tool. Upload a photo, get instant pest identification, risk assessment, and treatment recommendations.",
      keywords:
        "AI pest detection, pest identification AI, smart pest scanner, pest recognition tool UK, pest control AI demo",
      schemaType: "SoftwareApplication",
    },
    "/eco-recommendation-engine": {
      title: "Eco-Friendly Pest Control Recommendations | Greenshield",
      description:
        "Get personalized eco-friendly pest control treatment recommendations. Chemical-free and organic alternatives tailored to your pest problem and environment.",
      keywords:
        "eco pest control recommendations, organic pest treatment, green exterminator, sustainable pest solutions UK",
      schemaType: "Service",
    },
    "/prevention-coach": {
      title:
        "Pest Prevention Coach | Personalised Prevention Plans | Greenshield",
      description:
        "Work with our AI-powered pest prevention coach to build a tailored prevention plan for your property. Proactive pest management that saves money long-term.",
      keywords:
        "pest prevention coach, pest prevention plan, proactive pest control, pest management plan UK, prevent pests",
      schemaType: "Service",
    },
  },
};

// Generate canonical URL for current domain
export const generateCanonicalUrl = (
  path: string,
  brand: BrandType,
): string => {
  const domain =
    brand === "aegis360" ? "aegis360.co.uk" : "greenshieldpm.co.uk";
  const protocol = "https://";
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${protocol}www.${domain}${cleanPath}`;
};

// Get SEO config for current route and brand
export const getSEOConfig = (path: string, brand: BrandType): SEOConfig => {
  const config = brandSeoConfig[brand][path];

  if (config) {
    return {
      ...config,
      canonical: generateCanonicalUrl(path, brand),
    };
  }

  // Fallback SEO for unmatched routes
  const brandConfig =
    brand === "aegis360"
      ? {
          title: `Aegis360 - Modern Pest Control Business Software`,
          description:
            "Transform your pest control business with Aegis360's AI-powered SaaS platform.",
          keywords:
            "pest control software, pest management software, business automation",
          schemaType: "SoftwareApplication" as const,
        }
      : {
          title: `Greenshield Pest Management - Professional Pest Control UK`,
          description:
            "Fast, effective, and eco-friendly pest control services across the UK.",
          keywords: "pest control UK, exterminator, pest removal services",
          schemaType: "LocalBusiness" as const,
        };

  return {
    ...brandConfig,
    canonical: generateCanonicalUrl(path, brand),
  };
};

// Routes that should be excluded from sitemaps
export const excludeFromSitemap = [
  "/admin",
  "/admin-command-center",
  "/backend-admin",
  "/dashboard",
  "/profile",
  "/notification-preferences",
  "/access-control",
  "/feature-flags",
  "/cms-admin",
  "/workflow-admin",
  "/pricing-admin",
];

// Get all public routes for sitemap generation
export const getPublicRoutes = (brand: BrandType): string[] => {
  const routes = Object.keys(brandSeoConfig[brand]);
  return routes.filter(
    (route) =>
      !excludeFromSitemap.some((excluded) => route.startsWith(excluded)),
  );
};
